import { useMemo } from 'react';

import { createSafeContext } from './createSafeContext';
import { FeatureComponentProps, FeatureContext, FeaturesList, UseCreateFeatureFlag } from './types';

const featureFactory = <
  TList extends keyof FeaturesList,
  /** The props that are passed to the feature provider component when used */
  TExtendedProps extends Record<string, unknown> | undefined = Record<string, unknown>,
  /** The props that are passed to the useFeatureFlag hook or component when used */
  TDynamicUseProps extends Record<string, unknown> | undefined = Record<string, unknown>,
>() => {
  type ContextType = TExtendedProps & FeatureContext<TList>;
  const [FeatureProvider, useFeaturesContext] = createSafeContext<ContextType>(
    'Feature Context component was not found in tree'
  );

  const useFeatureFlag = ({
    featureId,
    additionalArgs = {} as TDynamicUseProps,
  }: UseCreateFeatureFlag<TList, TDynamicUseProps>) => {
    const ctx = useFeaturesContext();

    const useFeatures = typeof ctx.features === 'function' ? ctx.features : () => null;

    const featureFnValue = useFeatures(featureId);

    const useIsFeatureEnabledValue =
      typeof ctx.features !== 'function' ? ctx.features[featureId] : () => null;

    const featureEnabledValue = useIsFeatureEnabledValue({ ...additionalArgs, featureId });

    const { isLoading, isEnabled } = useMemo(() => {
      if (featureFnValue) {
        return featureFnValue;
      } else if (featureEnabledValue) {
        return featureEnabledValue;
      } else {
        return { isLoading: false, isEnabled: false };
      }
    }, [featureEnabledValue, featureFnValue]);

    const isFeatureEnabled = useMemo(() => {
      return isEnabled;
    }, [isEnabled]);

    return { isFeatureEnabled: isFeatureEnabled, isLoading };
  };

  /** Wraps the component in a featureflag id and additional props to prevent react state queue errors on key change */
  const ComponentWrapper = ({
    children,
    fallBack,
    featureId,
    showOnFalse = false, // We could allow it to inverse if needed, as in using this as a backup for a feature
    spinnerOnLoading = false,
    additionalProps = {} as TDynamicUseProps,
  }: FeatureComponentProps<TList, TDynamicUseProps>) => {
    const { isFeatureEnabled, isLoading } = useFeatureFlag({
      featureId,
      additionalArgs: additionalProps,
    });

    fallBack ??= null; // If no fallback is provided, default to null

    if (isLoading) return null; // If we are still loading, we don't want to show anything

    if (showOnFalse) {
      return <>{isFeatureEnabled == false ? children : fallBack}</>;
    }

    return <>{isFeatureEnabled ? children : fallBack}</>;
  };

  const IfFeatureState = (props: FeatureComponentProps<TList, TDynamicUseProps>) => {
    return <ComponentWrapper key={props.featureId} {...props} />;
  };

  return {
    IfFeatureState: IfFeatureState,
    FeatureProvider,
    useFeatureFlag: useFeatureFlag,
    useFeaturesContext,
  };
};

export { featureFactory };
